import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress } from '@mui/material';
import { MemreButton, MemreFlexBox } from 'components/core';
import * as Color from 'core/colors';

/**
 * Props for the UnenrollConfirmationDialog component
 */
interface UnenrollConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Whether an unenroll operation is in progress */
  isLoading: boolean;
  /** The name of the course being unenrolled from */
  courseName: string;
  /** Callback when the user confirms unenrollment */
  onConfirm: () => void;
  /** Callback when the user cancels unenrollment */
  onCancel: () => void;
}

/**
 * A dialog component for confirming course unenrollment
 *
 * Displays a confirmation dialog with course name and warning about lost progress
 */
const UnenrollConfirmationDialog: React.FC<UnenrollConfirmationDialogProps> = ({
  open,
  isLoading,
  courseName,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onCancel}
      aria-labelledby="unenroll-confirmation-title"
      aria-describedby="unenroll-confirmation-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="unenroll-confirmation-title">Unenroll from course</DialogTitle>
      <DialogContent>
        <DialogContentText id="unenroll-confirmation-description">
          Are you sure you want to unenroll from <strong>{courseName}</strong>?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <MemreFlexBox direction="row" gap={2} justify="flex-end" sx={{ p: 2 }}>
          <MemreButton text="Cancel" variant="outlined" color="primary" onClick={onCancel} disabled={isLoading} />
          <MemreButton
            text={isLoading ? 'Unenrolling...' : 'Unenroll'}
            variant="contained"
            color="warning"
            iconName="trash"
            onClick={onConfirm}
            disabled={isLoading}
            sx={{
              position: 'relative',
              ...(isLoading && { pl: 4 }), // Add extra padding when loading to accommodate spinner
            }}
          />
          {isLoading && (
            <CircularProgress
              size={20}
              sx={{
                color: Color.white,
                position: 'absolute',
                left: '16px',
              }}
            />
          )}
        </MemreFlexBox>
      </DialogActions>
    </Dialog>
  );
};

export default UnenrollConfirmationDialog;
